import MoreVertIcon from '@mui/icons-material/MoreVert'
import { CircularProgress, IconButton, Menu, MenuItem } from '@mui/material'
import { useState } from 'react'

import { intakeFormEnabledActionLabel } from '../../utils/intakeFormEnabledCopy'

export interface IntakeFormActionsMenuProps {
  /** Names the form in the trigger's label, which is otherwise identical on every row. */
  formName: string
  /** Decides both what the action is called and which way it goes. */
  isFormEnabled: boolean
  /** This form's own change is in flight, which is what the spinner is for. */
  isActionPending: boolean
  /** No change can be started right now, whichever form it would be for. */
  isActionDisabled: boolean
  onSelectAction: () => void
}

const PROGRESS_SIZE = 16

/**
 * The actions available on one intake form.
 *
 * A menu rather than a button on the row, because switching a form off takes it
 * away from patients and should not be a stray click on a list being skimmed.
 *
 * Closes before the action runs, so the confirmation is not raised underneath it.
 */
export const IntakeFormActionsMenu = ({
  formName,
  isFormEnabled,
  isActionPending,
  isActionDisabled,
  onSelectAction,
}: IntakeFormActionsMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isOpen = Boolean(anchorEl)

  const handleSelect = () => {
    setAnchorEl(null)
    onSelectAction()
  }

  return (
    <>
      <IconButton
        size='small'
        aria-label={`Actions for ${formName}`}
        aria-haspopup='true'
        aria-expanded={isOpen}
        disabled={isActionPending || isActionDisabled}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        {isActionPending ? (
          <CircularProgress size={PROGRESS_SIZE} />
        ) : (
          <MoreVertIcon style={{ fontSize: PROGRESS_SIZE }} />
        )}
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={() => setAnchorEl(null)}
        slotProps={{ paper: { variant: 'actionMenu' } }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          variant={isFormEnabled ? 'destructiveAction' : 'restorativeAction'}
          onClick={handleSelect}
        >
          {intakeFormEnabledActionLabel(isFormEnabled)}
        </MenuItem>
      </Menu>
    </>
  )
}
